import type { PostCategoryRef } from '@mg/shared';
import { cn } from '../../lib/cn';
import { CategoryDot } from './CategoryDot';

/**
 * A post's scheduling category, as the All Posts table, the campaign cards and the Trash show it: the
 * category's dot and name on a tint of its colour, or a neutral "General" pill (no dot) for a post with
 * no category. Display only.
 *
 * The tint is the category colour at 14% over whatever is behind it, so the ink text reads in both
 * themes (at least 14.8:1 light and 10.7:1 dark for every palette colour). `undefined` (not loaded) is
 * shown as General too: there is nothing else to show.
 */
export function CategoryPill({
  category,
  className,
}: {
  category: PostCategoryRef | null | undefined;
  className?: string;
}) {
  if (!category) {
    return (
      <span
        className={cn(
          'inline-flex items-center whitespace-nowrap rounded-full bg-chip px-2 py-0.5 text-xs font-medium text-ink-muted',
          className,
        )}
      >
        General
      </span>
    );
  }
  return (
    <span
      title={category.name}
      className={cn(
        'inline-flex min-w-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium text-ink',
        className,
      )}
      style={{ backgroundColor: `color-mix(in srgb, ${category.color} 14%, transparent)` }}
    >
      <CategoryDot color={category.color} />
      <span className="max-w-[10rem] truncate">{category.name}</span>
    </span>
  );
}
